/*************************
 * Author : Matthieu ROBIN
 *************************/
import React, { FunctionComponent, useEffect } from "react";

import {
  Alert,
  AlertTitle,
  Box,
  Fab,
  Grid2 as Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Zoom,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CarCardSkeleton from "../components/CarCardSkeleton";
import CarCard from "../components/CarCard";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchCars,
  selectAllCars,
  selectCarsError,
  selectCarsStatus,
  selectFilters,
  syncCars,
} from "../slices/cars-slice";
import { useNavigate } from "react-router";
import img from "../images/500_internal_error.svg";
import { CarFilters } from "../components/CarFilters";
import CarRow from "../components/CarRow";
import { GridView, TableRows } from "@mui/icons-material";

const CarListPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cars = useAppSelector(selectAllCars);
  const carsStatus = useAppSelector(selectCarsStatus);
  const carsError = useAppSelector(selectCarsError);

  const [displayType, setDisplayType] = React.useState<string | null>("cards");

  useEffect(() => {
    dispatch(fetchCars());
  }, []);

  const addCar = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    navigate(`/cars/add${queryParams}`);
  };

  const handleDisplayType = (
    _event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null,
  ) => {
    if (newAlignment !== null) {
      setDisplayType(newAlignment);
    }
  };

  const selledCars = cars.filter((car) => car.status === "Vendu");

  const totalPurchase = cars
    .map((car) => car.purchasePrice)
    .reduce((a, b) => a + b, 0);

  const totalNetSale = selledCars
    .map(
      (car) =>
        car.sell.trueSellPrice +
        car.sell.shippingCosts -
        car.sell.trueShippingCosts -
        car.sell.fees,
    )
    .reduce((a, b) => a + b, 0);

  const totalGain = totalNetSale - totalPurchase;

  if (carsError) {
    return (
      <div className="center" style={{ marginTop: 50 }}>
        <img alt="Erreur interne" src={img} width="200" />
        <Alert severity="error">
          <AlertTitle>Une erreur est survenue !</AlertTitle>
          {carsError.message}
        </Alert>
      </div>
    );
  }

  return (
    <Grid spacing={2} container>
      {carsStatus === "succeeded" ? (
        <>
          <Grid size={12}>
            <Box display="flex" flexDirection="row">
              <Box flexGrow={1}>
                <CarFilters />
              </Box>
              <ToggleButtonGroup
                value={displayType}
                exclusive
                onChange={handleDisplayType}
              >
                <ToggleButton value="cards">
                  <GridView />
                </ToggleButton>
                <ToggleButton value="table" aria-label="right aligned">
                  <TableRows />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          <Grid size={12}>
            <Typography variant="body2">
              {cars.length} élements correspondants aux critères
            </Typography>
          </Grid>
          {displayType === "cards" ? (
            cars.map((car) => (
              <Grid key={car._id} size={{ xl: 3, lg: 4, sm: 6, xs: 12 }}>
                <CarCard car={car} />
              </Grid>
            ))
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Véhicule</TableCell>
                    <TableCell>Compétition</TableCell>
                    <TableCell>Pilotes</TableCell>
                    <TableCell>Fabricant</TableCell>
                    <TableCell>Prix d'achat (€)</TableCell>
                    <TableCell>Vente nette (€)</TableCell>
                    <TableCell>+/- value (€)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cars.map((car) => (
                    <CarRow key={car._id} car={car} />
                  ))}
                </TableBody>
                <TableRow>
                  <TableCell align="right" colSpan={5}>
                    <b>TOTAL (€):</b>
                  </TableCell>
                  <TableCell>
                    {totalPurchase
                      .toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      .replace(/ /g, " ")}
                  </TableCell>
                  <TableCell>
                    {totalNetSale
                      .toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      .replace(/ /g, " ")}
                  </TableCell>
                  <TableCell
                    style={{
                      color:
                        totalGain != undefined
                          ? totalGain >= 0
                            ? "green"
                            : "red"
                          : "inherit",
                    }}
                  >
                    {totalGain
                      .toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      .replace(/ /g, " ")}
                  </TableCell>
                </TableRow>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        [0, 1, 2, 3].map((e) => (
          <Grid key={`${e}`} size={{ xl: 3, lg: 4, sm: 6, xs: 12 }}>
            <CarCardSkeleton />
          </Grid>
        ))
      )}

      <Zoom
        style={{
          transitionDelay: `300ms`,
        }}
        in
        unmountOnExit
      >
        <Fab
          aria-label="add"
          color="primary"
          onClick={addCar}
          style={{ position: "fixed", bottom: "25px", right: "25px" }}
        >
          <AddIcon />
        </Fab>
      </Zoom>
    </Grid>
  );
};

export default CarListPage;
